import React, { useState, useEffect } from "react";
import {
  Alert,
  Button,
  List,
  Modal,
  Select,
  Typography,
  Card,
  Space,
  Empty,
  App,
  Progress,
} from "antd";
import {
  PrinterOutlined,
  DeleteOutlined,
  PlusOutlined,
  SearchOutlined,
  FileTextOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import {
  SavedPrinter,
  PrinterType,
  PrinterTypeOptions,
  UsbDevice,
  NetworkPrinter,
} from "../models/printer";
import { addPrinter, deletePrinter, getSavedPrinters } from "../services/printerDb";
import { devNetworkPrinters, devUsbPrinters } from "../services/devPrinters";
import theme from "../config/theme";
import { scanPrinters, printTestPage } from "../shared/utils/Print";
import { useNotification } from "../shared/hooks";

const { Title, Text } = Typography;

export function PrinterManagementPage() {
  const { t } = useTranslation();
  const { showSuccess, showError, notifyError } = useNotification();
  const [savedPrinters, setSavedPrinters] = useState<SavedPrinter[]>([]);
  const [availableUsbPrinters, setAvailableUsbPrinters] = useState<UsbDevice[]>([]);
  const [availableNetworkPrinters, setAvailableNetworkPrinters] = useState<NetworkPrinter[]>([]);
  const [filteredUsbPrinters, setFilteredUsbPrinters] = useState<UsbDevice[]>([]);
  const [filteredNetworkPrinters, setFilteredNetworkPrinters] = useState<NetworkPrinter[]>([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedPrinter, setSelectedPrinter] = useState<
    { type: "usb"; printer: UsbDevice } | { type: "network"; printer: NetworkPrinter } | null
  >(null);
  const [selectedPrinterType, setSelectedPrinterType] = useState<PrinterType>(PrinterType.Ticket);
  const [isScanning, setIsScanning] = useState(false);
  const [scanProgress, setScanProgress] = useState(0);
  const [printingTestId, setPrintingTestId] = useState<string | null>(null);

  useEffect(() => {
    loadSavedPrinters();
    loadAvailablePrinters();
  }, []);

  const handleScanPrinters = () => {
    setIsScanning(true);
    setScanProgress(0);

    scanPrinters();

    const totalDuration = 10000; // 10 seconds

    const interval = setInterval(() => {
      setScanProgress((prev) => {
        if (prev >= 100) {
          return 100;
        }
        // Random progress increment
        const increment = Math.random() * 10;
        return Math.min(prev + increment, 100);
      });
    }, 900);

    setTimeout(() => {
      clearInterval(interval);
      setScanProgress(100);
      setTimeout(() => {
        setIsScanning(false);
        loadAvailablePrinters();
        showSuccess(t("printer_scan_complete"));
      }, 500);
    }, totalDuration);
  };

  const getUsbPrinterId = (printer: UsbDevice) =>
    `usb:${printer.id.id_vendor}:${printer.id.id_product}:${printer.id.serial}`;
  const getNetworkPrinterId = (printer: NetworkPrinter) => `net:${printer.mac}`;

  // Only one printer per type may be saved at a time.
  const savedPrinterByType = new Map(savedPrinters.map((printer) => [printer.type, printer]));
  const isTypeTaken = (type: PrinterType) => savedPrinterByType.has(type);
  const allTypesTaken = PrinterTypeOptions.every((option) => isTypeTaken(option.value));

  useEffect(() => {
    const savedPrinterIds = new Set(savedPrinters.map((p) => p.id));

    const newFilteredUsbPrinters = availableUsbPrinters.filter(
      (usbPrinter) => !savedPrinterIds.has(getUsbPrinterId(usbPrinter))
    );
    setFilteredUsbPrinters(newFilteredUsbPrinters);

    const newFilteredNetworkPrinters = availableNetworkPrinters.filter(
      (networkPrinter) => !savedPrinterIds.has(getNetworkPrinterId(networkPrinter))
    );
    setFilteredNetworkPrinters(newFilteredNetworkPrinters);
  }, [savedPrinters, availableUsbPrinters, availableNetworkPrinters]);

  async function loadSavedPrinters() {
    try {
      const printers = await getSavedPrinters();
      setSavedPrinters(printers);
    } catch (error) {
      notifyError(t("failed_to_load_connected_printers"), error);
    }
  }

  async function loadAvailablePrinters() {
    try {
      const response = await fetch("http://localhost:27245/v1/printers");
      if (!response.ok) {
        throw new Error("Failed to fetch available printers");
      }
      const data = await response.json();
      setAvailableUsbPrinters([...devUsbPrinters(), ...(data.usb_printers || [])]);
      setAvailableNetworkPrinters([...devNetworkPrinters(), ...(data.network_printers || [])]);
    } catch (error) {
      // It's okay if this fails, the printer agent might not be running.
      setAvailableUsbPrinters(devUsbPrinters());
      setAvailableNetworkPrinters(devNetworkPrinters());
    }
  }

  async function handleAddPrinter() {
    if (selectedPrinter) {
      if (isTypeTaken(selectedPrinterType)) {
        showError(t("printer_type_already_added", { type: selectedPrinterType }));
        return;
      }
      try {
        let newPrinter: SavedPrinter;
        if (selectedPrinter.type === "usb") {
          newPrinter = {
            id: `usb:${selectedPrinter.printer.id.id_vendor}:${selectedPrinter.printer.id.id_product}:${selectedPrinter.printer.id.serial}`,
            usb: selectedPrinter.printer,
            network: null,
            type: selectedPrinterType,
          };
        } else {
          newPrinter = {
            id: `net:${selectedPrinter.printer.mac}`,
            usb: null,
            network: selectedPrinter.printer,
            type: selectedPrinterType,
          };
        }
        await addPrinter(newPrinter);
        showSuccess(t("printer_added_successfully"));
        loadSavedPrinters();
        setIsModalVisible(false);
        setSelectedPrinter(null);
      } catch (error) {
        notifyError(t("failed_to_add_printer"), error);
      }
    }
  }

  async function handleDeletePrinter(id: string) {
    try {
      await deletePrinter(id);
      showSuccess(t("printer_deleted_successfully"));
      loadSavedPrinters();
    } catch (error) {
      notifyError(t("failed_to_delete_printer"), error);
    }
  }

  function handlePrintTestPage(printer: SavedPrinter) {
    setPrintingTestId(printer.id);
    printTestPage(
      printer,
      () => {
        setPrintingTestId(null);
        showSuccess(t("test_page_printed_successfully"));
      },
      (error) => {
        setPrintingTestId(null);
        notifyError(t("failed_to_print_test_page"), error);
      }
    );
  }

  function openAddPrinterModal(
    printer: { type: "usb"; printer: UsbDevice } | { type: "network"; printer: NetworkPrinter }
  ) {
    if (allTypesTaken) {
      showError(t("all_printer_types_already_added"));
      return;
    }
    const firstAvailableType = PrinterTypeOptions.find((option) => !isTypeTaken(option.value));
    if (firstAvailableType) {
      setSelectedPrinterType(firstAvailableType.value);
    }
    setSelectedPrinter(printer);
    setIsModalVisible(true);
  }

  function showAddUsbPrinterModal(printer: UsbDevice) {
    openAddPrinterModal({ type: "usb", printer });
  }
  function showAddNetworkPrinterModal(printer: NetworkPrinter) {
    openAddPrinterModal({ type: "network", printer });
  }

  return (
    <Space direction="vertical" style={{ width: "100%", gap: theme.spacing.xl }}>
      <Button
        type="primary"
        icon={<SearchOutlined />}
        onClick={handleScanPrinters}
        loading={isScanning}
      >
        {t("scan_for_new_printers")}
      </Button>
      {/* Saved Printers Section */}
      <Card
        title={
          <Space>
            <PrinterOutlined
              style={{ fontSize: theme.iconSizes.md, color: theme.colors.primary.main }}
            />
            <Text strong style={{ fontSize: theme.typography.fontSize.lg }}>
              {t("saved_printers")}
            </Text>
          </Space>
        }
        style={{
          borderRadius: theme.borderRadius.xl,
          boxShadow: theme.shadows.md,
          border: `1px solid ${theme.colors.neutral.gray200}`,
        }}
        styles={{ body: { padding: theme.spacing.xl } }}
      >
        {savedPrinters.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={<Text type="secondary">{t("no_saved_printers")}</Text>}
          />
        ) : (
          <List
            itemLayout="horizontal"
            dataSource={savedPrinters}
            renderItem={(printer) => (
              <List.Item
                style={{
                  padding: theme.spacing.lg,
                  borderRadius: theme.borderRadius.lg,
                  marginBottom: theme.spacing.sm,
                  background: theme.colors.neutral.gray50,
                }}
                actions={[
                  <Button
                    icon={<FileTextOutlined />}
                    onClick={() => handlePrintTestPage(printer)}
                    loading={printingTestId === printer.id}
                    key="test"
                    style={{
                      borderRadius: theme.borderRadius.lg,
                      height: theme.layout.button.height,
                    }}
                  >
                    {t("print_test_page")}
                  </Button>,
                  <Button
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => handleDeletePrinter(printer.id)}
                    key="delete"
                    style={{
                      borderRadius: theme.borderRadius.lg,
                      height: theme.layout.button.height,
                    }}
                  >
                    {t("delete")}
                  </Button>,
                ]}
              >
                <List.Item.Meta
                  avatar={
                    <PrinterOutlined
                      style={{ fontSize: theme.iconSizes.xl, color: theme.colors.primary.main }}
                    />
                  }
                  title={
                    <Text strong style={{ fontSize: theme.typography.fontSize.lg }}>
                      {printer.usb
                        ? `${printer.usb.manufacturer} ${printer.usb.product}`
                        : `${printer.network?.vendor} ${printer.network?.ip}`}
                    </Text>
                  }
                  description={
                    <Space direction="vertical" size="small">
                      <Text type="secondary">
                        <Text strong>{t("type")}:</Text> {printer.type}
                      </Text>
                      {printer.usb ? (
                        <>
                          <Text type="secondary">
                            <Text strong>{t("serial")}:</Text> {printer.usb.id.serial}
                          </Text>
                          <Text type="secondary" style={{ fontSize: theme.typography.fontSize.xs }}>
                            {t("vendor_id")}: {printer.usb.id.id_vendor} | {t("product_id")}:{" "}
                            {printer.usb.id.id_product}
                          </Text>
                        </>
                      ) : (
                        <>
                          <Text type="secondary">
                            <Text strong>MAC:</Text> {printer.network?.mac}
                          </Text>
                          <Text type="secondary" style={{ fontSize: theme.typography.fontSize.xs }}>
                            Port: {printer.network?.port}
                          </Text>
                        </>
                      )}
                    </Space>
                  }
                />
              </List.Item>
            )}
          />
        )}
      </Card>

      {/* Available USB Printers Section */}
      <Card
        title={
          <Space>
            <PlusOutlined style={{ fontSize: theme.iconSizes.md, color: theme.colors.success }} />
            <Text strong style={{ fontSize: theme.typography.fontSize.lg }}>
              {t("add_a_usb_printer")}
            </Text>
          </Space>
        }
        style={{
          borderRadius: theme.borderRadius.xl,
          boxShadow: theme.shadows.md,
          border: `1px solid ${theme.colors.neutral.gray200}`,
        }}
        styles={{ body: { padding: theme.spacing.xl } }}
      >
        {filteredUsbPrinters.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <Space direction="vertical">
                <Text type="secondary">{t("no_available_printers")}</Text>
                <Text type="secondary" style={{ fontSize: theme.typography.fontSize.sm }}>
                  {t("make_sure_printer_plugged_in")}
                </Text>
              </Space>
            }
          />
        ) : (
          <List
            itemLayout="horizontal"
            dataSource={filteredUsbPrinters}
            renderItem={(printer) => (
              <List.Item
                style={{
                  padding: theme.spacing.lg,
                  borderRadius: theme.borderRadius.lg,
                  marginBottom: theme.spacing.sm,
                  background: theme.colors.neutral.gray50,
                }}
                actions={[
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => showAddUsbPrinterModal(printer)}
                    key="add"
                    style={{
                      borderRadius: theme.borderRadius.lg,
                      height: theme.layout.button.height,
                    }}
                  >
                    {t("add")}
                  </Button>,
                ]}
              >
                <List.Item.Meta
                  avatar={
                    <PrinterOutlined
                      style={{ fontSize: theme.iconSizes.xl, color: theme.colors.success }}
                    />
                  }
                  title={
                    <Text strong style={{ fontSize: theme.typography.fontSize.lg }}>
                      {`${printer.manufacturer} ${printer.product}`}
                    </Text>
                  }
                  description={
                    <Space direction="vertical" size="small">
                      <Text type="secondary">
                        <Text strong>{t("serial")}:</Text> {printer.id.serial}
                      </Text>
                      <Text type="secondary" style={{ fontSize: theme.typography.fontSize.xs }}>
                        {t("vendor_id")}: {printer.id.id_vendor} | {t("product_id")}:{" "}
                        {printer.id.id_product}
                      </Text>
                    </Space>
                  }
                />
              </List.Item>
            )}
          />
        )}
      </Card>

      {/* Available Network Printers Section */}
      <Card
        title={
          <Space>
            <PlusOutlined style={{ fontSize: theme.iconSizes.md, color: theme.colors.success }} />
            <Text strong style={{ fontSize: theme.typography.fontSize.lg }}>
              {t("add_a_network_printer")}
            </Text>
          </Space>
        }
        style={{
          borderRadius: theme.borderRadius.xl,
          boxShadow: theme.shadows.md,
          border: `1px solid ${theme.colors.neutral.gray200}`,
        }}
        styles={{ body: { padding: theme.spacing.xl } }}
      >
        {filteredNetworkPrinters.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <Space direction="vertical">
                <Text type="secondary">{t("no_available_printers")}</Text>
                <Text type="secondary" style={{ fontSize: theme.typography.fontSize.sm }}>
                  {t("make_sure_printer_plugged_in")}
                </Text>
              </Space>
            }
          />
        ) : (
          <List
            itemLayout="horizontal"
            dataSource={filteredNetworkPrinters}
            renderItem={(printer) => (
              <List.Item
                style={{
                  padding: theme.spacing.lg,
                  borderRadius: theme.borderRadius.lg,
                  marginBottom: theme.spacing.sm,
                  background: theme.colors.neutral.gray50,
                }}
                actions={[
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => showAddNetworkPrinterModal(printer)}
                    key="add"
                    style={{
                      borderRadius: theme.borderRadius.lg,
                      height: theme.layout.button.height,
                    }}
                  >
                    {t("add")}
                  </Button>,
                ]}
              >
                <List.Item.Meta
                  avatar={
                    <PrinterOutlined
                      style={{ fontSize: theme.iconSizes.xl, color: theme.colors.success }}
                    />
                  }
                  title={
                    <Text strong style={{ fontSize: theme.typography.fontSize.lg }}>
                      {`${printer.vendor} ${printer.ip}`}
                    </Text>
                  }
                  description={
                    <Space direction="vertical" size="small">
                      <Text type="secondary">
                        <Text strong>MAC:</Text> {printer.mac}
                      </Text>
                      <Text type="secondary" style={{ fontSize: theme.typography.fontSize.xs }}>
                        Port: {printer.port}
                      </Text>
                    </Space>
                  }
                />
              </List.Item>
            )}
          />
        )}
      </Card>

      {/* Add Printer Modal */}
      <Modal
        title={
          <Title level={4} style={{ margin: 0 }}>
            {t("select_printer_type")}
          </Title>
        }
        open={isModalVisible}
        onOk={handleAddPrinter}
        onCancel={() => setIsModalVisible(false)}
        okText={t("add")}
        cancelText={t("cancel")}
        okButtonProps={{
          disabled: isTypeTaken(selectedPrinterType),
          style: {
            borderRadius: theme.borderRadius.lg,
            height: theme.layout.button.height,
          },
        }}
        cancelButtonProps={{
          style: {
            borderRadius: theme.borderRadius.lg,
            height: theme.layout.button.height,
          },
        }}
      >
        <Space direction="vertical" style={{ width: "100%", gap: theme.spacing.md }}>
          <Text type="secondary">{t("select_type_for_printer")}</Text>
          <Select
            value={selectedPrinterType}
            style={{ width: "100%" }}
            onChange={(value) => setSelectedPrinterType(value)}
            size="large"
            options={PrinterTypeOptions.map((option) => ({
              key: option.value,
              value: option.value,
              label: isTypeTaken(option.value)
                ? `${option.label} — ${t("printer_type_already_added_short")}`
                : option.label,
            }))}
          />
          {isTypeTaken(selectedPrinterType) && (
            <Alert
              type="error"
              showIcon
              message={t("printer_type_already_added", { type: selectedPrinterType })}
            />
          )}
          <Text type="secondary" style={{ fontSize: theme.typography.fontSize.sm }}>
            {t("only_one_printer_per_type")}
          </Text>
        </Space>
      </Modal>

      {/* Scanning progress Modal */}
      <Modal title={t("scanning_for_printers")} open={isScanning} footer={null} closable={false}>
        <div style={{ padding: `${theme.spacing.lg}px 0` }}>
          <Progress percent={Math.round(scanProgress)} />
          <Text
            type="secondary"
            style={{ textAlign: "center", display: "block", marginTop: theme.spacing.md }}
          >
            {t("this_will_take_around_10_seconds")}
          </Text>
        </div>
      </Modal>
    </Space>
  );
}
